import { useState } from 'react'
import { ClientValidationWrapper,
  ConfirmationMessage,
  DocumentsList, DocumentWrapper, DownloadLink, ValidateClient, ValidationChoices } from './styles'
import { useTranslation } from 'react-i18next'
import { Domiciliation, LegalDocumentCategory } from 'globals/models/Domiciliation'
import { OkIcon, DownloadIcon2 } from 'components/Icons'
import GraphQLHandler from 'components/GraphQLHandler'
import { useLazyQuery, useMutation } from '@apollo/client'
import {
  CUSTOMER_ZIP,
  CustomerZipResult,
  CustomerZipVariables
} from 'pages/ClientDetails/components/ClientActionsTab/queries'
import { Radio } from 'antd'
import RadioGroup from 'components/RadioGroup'
import { TFunction } from 'i18next'
import * as Yup from 'yup'
import { TestFunction } from 'yup'
import { Formik } from 'formik'
import { displayErrors, DoMutation, hasNoError } from 'globals/graphql'
import {
  DOMICILIATION_VALIDATION,
  DomiciliationValidationResult,
  DomiciliationValidationVariables
} from 'pages/ClientDetails/components/ClientActionsTab/components/ClientValidation/mutations'
import { SetState } from 'globals'
import SubmitButton from 'components/SubmitButton'
import Input from 'components/Input'
import Dot from 'components/Dot'
import colors from 'globals/colors'

interface ClientValidationForm {
  validated?: boolean,
  comment?: string
}

const initialValues: ClientValidationForm = {}

const validation = (t: TFunction): Yup.SchemaOf<ClientValidationForm> => Yup.object({
  validated: Yup.boolean()
      .required(t('global.error.required', { field: t('global.choice') })),
  comment: Yup.string()
      .test('validation-comment-test', t('global.error.required', { field: t('global.comment') }), validateComment)
})

interface IProps {
  domiciliation: Domiciliation
  customerZipRetrieved: any
}

const ClientValidation = (props: IProps) => {
  const { t } = useTranslation()
  const { domiciliation, customerZipRetrieved } = props
  const [validated, setValidated] = useState<boolean>()

  return (
    <GraphQLHandler
      lazyQueries={{
        customerZip: useLazyQuery<
        CustomerZipResult, CustomerZipVariables
        >(CUSTOMER_ZIP, { ...displayErrors, onCompleted: customerZipRetrieved })
      }}
      mutations={{
        domiciliationValidation: useMutation<
        DomiciliationValidationResult, DomiciliationValidationVariables
        >(DOMICILIATION_VALIDATION, displayErrors)
      }}
    >
      {({
        lazyQueries: {
          customerZip: [getCustomerZip]
        },
        mutations: {
          domiciliationValidation: [doDomiciliationValidation]
        }
      }) =>
        <Formik
          initialValues={initialValues}
          validationSchema={validation(t)}
          onSubmit={onValidateClient(doDomiciliationValidation, setValidated, domiciliation)}
          isInitialValid={validation(t).isValidSync(initialValues)}
        >
          {({ values }) =>
            <ClientValidationWrapper>
              <DocumentsList>
                <b>{t('clients.actions.validation.waiting')}</b>

                {domiciliation.legalDocuments.map((doc, id) =>
                  <Document key={id} category={doc.category} />
                )}

                <DownloadLink
                  onClick={() => {
                    getCustomerZip({ variables: { domiciliationId: domiciliation.id, generateZip: true } })
                  }}>
                  <DownloadIcon2 />
                  <span>{t('clients.actions.validation.download', { nb: domiciliation.legalDocuments.length })}</span>
                </DownloadLink>
              </DocumentsList>

              <ValidateClient>
                <b>{t('clients.actions.validation.validate')} <Dot color={colors.reddishPink} /></b>

                <ValidationChoices>
                  <RadioGroup
                    name="validated"
                  >
                    <Radio value={true}>{t('global.yes')}</Radio>
                    <Radio value={false}>{t('global.no')}</Radio>
                  </RadioGroup>
                </ValidationChoices>

                {values.validated === false &&
                  <Input
                    name="comment"
                    type="multiline"
                    placeholder={t('clients.actions.validation.add-comment')}
                    rows={4}
                  />
                }

                <SubmitButton text={t('global.confirm')} />

                {validated !== undefined &&
                  <ConfirmationMessage validated={validated || false}>
                    {t('clients.actions.validation.' + (validated ? 'accepted' : 'refused'))}
                  </ConfirmationMessage>
                }
              </ValidateClient>
            </ClientValidationWrapper>
          }
        </Formik>
      }
    </GraphQLHandler>
  )
}

const validateComment: TestFunction<string | undefined> = (value: string | undefined, context) => {
  if ((context.parent as ClientValidationForm).validated === false && !value) {
    return false
  }

  return true
}

const onValidateClient = (
    doDomiciliationValidation: DoMutation<DomiciliationValidationResult, DomiciliationValidationVariables>,
    setValidated: SetState<boolean | undefined>,
    domiciliation: Domiciliation
) => (values: ClientValidationForm) =>
  doDomiciliationValidation({
    variables: {
      domiciliationId: domiciliation.id,
      validated: values.validated || false,
      message: values.comment
    }
  }).then(hasNoError(() =>
    setValidated(values.validated)
  ))

interface IDocumentProps {
  category: LegalDocumentCategory
}

const Document = (props: IDocumentProps) => {
  const { t } = useTranslation()

  return (
    <DocumentWrapper>
      <OkIcon /> {t(`clients.actions.validation.documents.${props.category}`)}
    </DocumentWrapper>
  )
}

export default ClientValidation
